import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { AnimatePresence, motion } from "framer-motion";
import { useReleases } from "./hooks/useReleases";
import useAdvancedFilters from "./hooks/useAdvancedFilters";
import useAdvancedSorting from "./hooks/useAdvancedSorting";
import useWatchlist from "./hooks/useWatchlist";
import NewHeader from "./components/NewHeader";
import NewFilterBar from "./components/NewFilterBar";
import NewShowsGrid from "./components/NewShowsGrid";
import NewShowsList from "./components/NewShowsList";
import CompactGrid from "./components/CompactGrid";
import DateGroupedReleases from "./components/DateGroupedReleases";
import ValueProposition, { isHeroExamplePairing } from "./components/ValueProposition";
import BrowsePremieresBand from "./components/BrowsePremieresBand";
import StructuredData from "./components/StructuredData";
import LoadingSpinner from "./components/LoadingSpinner";
import ErrorAlert from "./components/ErrorAlert";
import InsightsPanel from "./components/InsightsPanel";
import SiteFooter from "./components/SiteFooter";
import WhySection from "./components/WhySection";
import BackToTopButton from "./components/BackToTopButton";
import WatchlistShelf from "./components/WatchlistShelf";
import PairingsRail from "./components/PairingsRail";
import { handleInternalLinkClick } from "./utils/navigation";
import Typography from "./components/ui/Typography";
import usePageMeta from "./hooks/usePageMeta";
import { buildHomeSeoMeta } from "./pages/home/homeSeo";
import { trackEvent } from "./utils/analytics";
import useNow from "./hooks/useNow";
import {
    BROWSE_PREFERENCES_VERSION,
    BROWSE_PREFERENCES_VERSION_KEY,
    DEFAULT_DESKTOP_VIEW_MODE,
    DENSITY_STORAGE_KEY,
    VIEW_MODE_STORAGE_KEY,
    isValidDensity,
    isValidViewMode,
    resolveBrowsePreferences,
} from "./utils/browsePreferences";

const NewApp = ({ initialReleasesPayload = null }) => {
    const filterBarId = "homepage-filter-bar";
    // Date range and refresh state
    const forceRefresh = false;
    const dateRange = "next";
    const [quickDateRange, setQuickDateRange] = useState("all");
    const [highlightedShowId, setHighlightedShowId] = useState(null);
    const highlightTimeoutRef = useRef(null);

    // Compact is the browse-first default. Returning visitors keep their choice.
    const [viewMode, setViewMode] = useState(DEFAULT_DESKTOP_VIEW_MODE);

    // Density state with localStorage persistence
    const [density, setDensity] = useState("comfortable");

    // Detect mobile screen size to force grid view
    const [isMobile, setIsMobile] = useState(false);
    const [insightsOpen, setInsightsOpen] = useState(false);
    const browsePreferencesLoadedRef = useRef(false);

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }

        const storedViewMode = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
        const storedDensity = window.localStorage.getItem(DENSITY_STORAGE_KEY);
        const storedPreferencesVersion = window.localStorage.getItem(BROWSE_PREFERENCES_VERSION_KEY);
        const storedInsightsOpen = window.localStorage.getItem("tv-releases-insights-open");
        const persistedIsMobile = window.innerWidth < 768;
        const storedBrowsePreferences = resolveBrowsePreferences({
            storedViewMode,
            storedDensity,
            storedVersion: storedPreferencesVersion,
        });
        const rafId = window.requestAnimationFrame(() => {
            setViewMode(storedBrowsePreferences.viewMode);
            setDensity(storedBrowsePreferences.density);
            setInsightsOpen(storedInsightsOpen === "true");
            setIsMobile((prev) => (prev === persistedIsMobile ? prev : persistedIsMobile));
            window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, storedBrowsePreferences.viewMode);
            window.localStorage.setItem(DENSITY_STORAGE_KEY, storedBrowsePreferences.density);
            window.localStorage.setItem(BROWSE_PREFERENCES_VERSION_KEY, BROWSE_PREFERENCES_VERSION);
            browsePreferencesLoadedRef.current = true;
        });

        const handleResize = () => {
            const nextIsMobile = window.innerWidth < 768;
            setIsMobile((prev) => (prev === nextIsMobile ? prev : nextIsMobile));
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.cancelAnimationFrame(rafId);
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Effective view mode - force compact on mobile
    const effectiveViewMode = isMobile ? "compact" : viewMode;

    // Fetch releases using existing hook
    const {
        releases,
        loading,
        error,
        dateRangeLoading,
        referenceDay,
        lastUpdated,
        count,
        totalCount,
        candidateCount,
        stats,
        insights,
        ifYouLikedPairings,
    } = useReleases(forceRefresh, dateRange, initialReleasesPayload);

    // Advanced filtering
    const {
        filteredReleases,
        availableGenres,
        availableNetworks,
        availableContentRatings,
        searchQuery,
        setSearchQuery,
        selectedGenres,
        setSelectedGenres,
        selectedNetworks,
        setSelectedNetworks,
        selectedContentRatings,
        setSelectedContentRatings,
        seriesType,
        setSeriesType,
        resetFilters,
        hasActiveFilters,
    } = useAdvancedFilters(releases);

    const {
        entries: watchlistEntries,
        watchlistCount,
        isInWatchlist,
        toggleWatchlist,
        upsertWatchlistEntries,
    } = useWatchlist();
    const [watchlistOnly, setWatchlistOnly] = useState(false);
    const effectiveWatchlistOnly = watchlistOnly && watchlistCount > 0;

    const watchlistShows = useMemo(() => {
        if (!watchlistEntries.length) {
            return [];
        }
        const byId = new Map(releases.map((show) => [String(show.id), show]));
        return watchlistEntries
            .map((entry) => ({
                ...entry,
                ...(byId.get(String(entry.id)) || {}),
                addedAt: entry.addedAt,
            }))
            .filter(Boolean);
    }, [releases, watchlistEntries]);

    const watchlistFilteredReleases = useMemo(() => {
        if (!effectiveWatchlistOnly) {
            return filteredReleases;
        }
        return filteredReleases.filter((show) => isInWatchlist(show.id));
    }, [effectiveWatchlistOnly, filteredReleases, isInWatchlist]);

    const quickFilteredReleases = useMemo(() => {
        if (quickDateRange === "all") {
            return watchlistFilteredReleases;
        }

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextWeek = new Date(today);
        nextWeek.setDate(nextWeek.getDate() + 7);

        return watchlistFilteredReleases.filter((show) => {
            if (!show?.releaseDate) {
                return false;
            }
            const [year, month, day] = show.releaseDate.split("-").map(Number);
            if (!year || !month || !day) {
                return false;
            }
            const releaseDate = new Date(year, month - 1, day); // local date
            if (quickDateRange === "tonight") {
                return releaseDate >= today && releaseDate < tomorrow;
            }
            if (quickDateRange === "week") {
                return releaseDate >= today && releaseDate < nextWeek;
            }
            return true;
        });
    }, [quickDateRange, watchlistFilteredReleases]);

    // Advanced sorting
    const { sortedReleases, sortBy, handleSortChange } = useAdvancedSorting(
        quickFilteredReleases,
        "releaseDate"
    );
    const hasActiveFiltersExtended = hasActiveFilters || quickDateRange !== "all" || effectiveWatchlistOnly;

    const nowMs = useNow();
    const formatRelativeTime = useCallback((isoString) => {
        if (!isoString) {
            return null;
        }
        const updatedDate = new Date(isoString);
        if (Number.isNaN(updatedDate.getTime())) {
            return null;
        }
        const diffMs = updatedDate.getTime() - nowMs;
        const diffMinutes = Math.round(diffMs / (1000 * 60));

        const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });

        if (Math.abs(diffMinutes) < 60) {
            return rtf.format(diffMinutes, "minute");
        }

        const diffHours = Math.round(diffMinutes / 60);
        if (Math.abs(diffHours) < 24) {
            return rtf.format(diffHours, "hour");
        }

        const diffDays = Math.round(diffHours / 24);
        return rtf.format(diffDays, "day");
    }, [nowMs]);

    const insightsSummary = useMemo(() => ({
        count,
        totalCount,
        lastUpdated,
        relativeUpdated: formatRelativeTime(lastUpdated),
        newVsReturning: insights?.newVsReturning,
        trend: insights?.trends?.premiumCount,
    }), [count, totalCount, lastUpdated, formatRelativeTime, insights]);

    const recommendationPairings = useMemo(
        () => (Array.isArray(ifYouLikedPairings) ? ifYouLikedPairings : []),
        [ifYouLikedPairings]
    );
    // The hero shows one qualifying pairing as a live example; the desktop rail
    // shows the rest so the example isn't repeated.
    const heroPairing = useMemo(
        () => recommendationPairings.find(isHeroExamplePairing) || null,
        [recommendationPairings]
    );
    const secondaryPairings = useMemo(
        () => recommendationPairings.filter((pairing) => pairing !== heroPairing),
        [recommendationPairings, heroPairing]
    );

    const windowLabel = useMemo(() => {
        const formatter = new Intl.DateTimeFormat("en-GB", {
            month: "short",
            day: "numeric",
        });

        const today = new Date();
        const start = new Date(today);
        const end = new Date(today);

        if (dateRange === "previous") {
            start.setDate(today.getDate() - 30);
            end.setDate(today.getDate());
        } else if (dateRange === "next") {
            end.setDate(today.getDate() + 30);
        } else {
            end.setDate(today.getDate() + 30);
        }

        const description = dateRange === "previous"
            ? "Previous 30 days"
            : dateRange === "next"
                ? "Next 30 days"
                : "Rolling window";

        return {
            range: `${formatter.format(start)} – ${formatter.format(end)}`,
            description,
        };
    }, [dateRange]);

    const homepageSeo = useMemo(() => buildHomeSeoMeta({ data: releases }), [releases]);

    usePageMeta(homepageSeo);

    useEffect(() => {
        upsertWatchlistEntries(releases);
    }, [releases, upsertWatchlistEntries]);

    // Persist view mode to localStorage
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (!browsePreferencesLoadedRef.current) {
            return;
        }
        window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    }, [viewMode]);

    // Persist density to localStorage
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        if (!browsePreferencesLoadedRef.current) {
            return;
        }
        window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
    }, [density]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return undefined;
        }
        window.localStorage.setItem(
            "tv-releases-insights-open",
            insightsOpen ? "true" : "false"
        );
        return undefined;
    }, [insightsOpen]);

    useEffect(() => {
        return () => {
            if (highlightTimeoutRef.current) {
                clearTimeout(highlightTimeoutRef.current);
                highlightTimeoutRef.current = null;
            }
        };
    }, []);

    const handleQuickDateRangeChange = useCallback((newRange) => {
        setQuickDateRange((prev) => (prev === newRange ? "all" : newRange));
    }, []);

    const handleWatchlistToggle = useCallback(() => {
        setWatchlistOnly((prev) => !prev);
    }, []);

    const handleRandomPick = useCallback(() => {
        if (!sortedReleases.length) {
            return;
        }
        const pick = sortedReleases[Math.floor(Math.random() * sortedReleases.length)];
        const pickId = String(pick.id);

        setHighlightedShowId(pickId);
        if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
        }

        const scrollToPick = () => {
            const element = document.getElementById(`show-${pickId}`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
                return true;
            }
            return false;
        };

        if (!scrollToPick()) {
            setTimeout(scrollToPick, 300);
        }

        highlightTimeoutRef.current = setTimeout(() => {
            setHighlightedShowId(null);
            highlightTimeoutRef.current = null;
        }, 2400);
    }, [sortedReleases]);

    const handleResetFilters = useCallback(() => {
        resetFilters();
        setQuickDateRange("all");
        setWatchlistOnly(false);
    }, [resetFilters]);

    const toggleInsights = useCallback(() => {
        setInsightsOpen((prev) => !prev);
    }, []);

    const closeInsights = useCallback(() => {
        setInsightsOpen(false);
    }, []);

    const handleGenreToggle = useCallback((genre) => {
        if (!genre) {
            return;
        }
        setSelectedGenres((prev) => {
            if (prev.includes(genre)) {
                return prev.filter((item) => item !== genre);
            }
            return [...prev, genre];
        });
    }, [setSelectedGenres]);

    const handleNetworkToggle = useCallback((network) => {
        if (!network) {
            return;
        }
        setSelectedNetworks((prev) => {
            if (prev.includes(network)) {
                return prev.filter((item) => item !== network);
            }
            return [...prev, network];
        });
    }, [setSelectedNetworks]);

    const handleViewModeChange = useCallback((newViewMode) => {
        if (!isValidViewMode(newViewMode) || newViewMode === viewMode) {
            return;
        }
        trackEvent("homepage_view_mode_change", {
            from: viewMode,
            to: newViewMode,
        });
        setViewMode(newViewMode);
    }, [viewMode]);

    const handleDensityChange = useCallback((newDensity) => {
        if (!isValidDensity(newDensity) || newDensity === density) {
            return;
        }
        trackEvent("homepage_density_change", {
            from: density,
            to: newDensity,
            view_mode: viewMode,
        });
        setDensity(newDensity);
    }, [density, viewMode]);


    // Loading state
    if (loading && !releases.length) {
        return <LoadingSpinner />;
    }

    // Error state
    if (error) {
        return <ErrorAlert message={error} />;
    }

    const hasNoResults = !releases || releases.length === 0;
    const hasNoFilteredResults = sortedReleases.length === 0 && releases.length > 0;
    const archiveSearchHref = searchQuery.trim()
        ? `/archive?q=${encodeURIComponent(searchQuery.trim())}&mode=lexical`
        : "/archive";
    const useDateGrouping = sortBy === "releaseDate";
    const pairingsRail = hasActiveFiltersExtended ? null : (
        <PairingsRail
            pairings={isMobile ? recommendationPairings : secondaryPairings}
            title={isMobile ? "Shows you might like" : "More shows you might like"}
            subtitle={isMobile
                ? "Current premieres with a clear reason they may fit something you already like."
                : "Current premieres worth a closer look, each with a reason it may suit fans of a show you know."
            }
            minPairings={isMobile ? 1 : 2}
            limit={2}
            surface={isMobile
                ? "homepage_pairings_midlist_mobile_primary"
                : "homepage_pairings_midlist_desktop_secondary"
            }
        />
    );
    const renderReleaseCollection = (collection) => {
        if (effectiveViewMode === "compact") {
            return (
                <CompactGrid
                    releases={collection}
                    loading={dateRangeLoading}
                    showTitle={false}
                    highlightedShowId={highlightedShowId}
                    isInWatchlist={isInWatchlist}
                    onToggleWatchlist={toggleWatchlist}
                />
            );
        }

        if (effectiveViewMode === "list") {
            return (
                <NewShowsList
                    releases={collection}
                    loading={dateRangeLoading}
                    showTitle={false}
                    density={density}
                    highlightedShowId={highlightedShowId}
                    isInWatchlist={isInWatchlist}
                    onToggleWatchlist={toggleWatchlist}
                />
            );
        }

        return (
            <NewShowsGrid
                releases={collection}
                loading={dateRangeLoading}
                showTitle={false}
                density={density}
                highlightedShowId={highlightedShowId}
                isInWatchlist={isInWatchlist}
                onToggleWatchlist={toggleWatchlist}
            />
        );
    };
    return (
        <div className="min-h-screen relative pt-20 sm:pt-24">
            {/* Structured Data for SEO */}
            <StructuredData releases={sortedReleases} />
            
            {/* Header */}
            <NewHeader
                insightsSummary={insightsSummary}
                onInsightsToggle={toggleInsights}
                insightsOpen={insightsOpen}
                watchlistCount={watchlistCount}
            />

            <main id="main-content" tabIndex={-1} className="relative z-10">
                {/* Value Proposition - Always Visible */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-6 sm:mt-10">
                    <ValueProposition
                        examplePairing={heroPairing}
                    />
                </div>

                {/* Main Content */}
                <div className="route-shell route-shell--home space-y-6">
                {/* No releases at all */}
                {hasNoResults && (
                    <div className="text-center py-12 space-y-4">
                        <div className="text-muted-foreground mb-4">
                            <svg
                                className="mx-auto h-16 w-16"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1}
                                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                />
                            </svg>
                        </div>
                        <h2 className="text-h3 font-semibold text-foreground">
                            Nothing makes the cut right now
                        </h2>
                        <p className="text-body text-muted-foreground max-w-md mx-auto">
                            No scripted shows made the list for the {dateRange === "next" ? "next" : "previous"} 30 days.{" "}
                            <a href="/archive" className="font-semibold text-primary underline-offset-4 hover:underline">
                                Browse the archive
                            </a>{" "}
                            to find past premieres by mood instead.
                        </p>
                    </div>
                )}

                {/* Has releases - show filters and results */}
                {!hasNoResults && (
                    <>
                        {/* Browse premieres — the list's own header band */}
                        <BrowsePremieresBand
                            premiereCount={releases.length}
                            updatedAt={insightsSummary?.lastUpdated || lastUpdated || ""}
                        />

                        {/* Filter Bar */}
                        <NewFilterBar
                            filterBarId={filterBarId}
                            totalCount={releases.length}
                            // Search
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}

                            // Sort
                            sortBy={sortBy}
                            onSortChange={handleSortChange}

                            // View mode (hide switcher on mobile)
                            viewMode={effectiveViewMode}
                            onViewModeChange={isMobile ? undefined : handleViewModeChange}

                            // Density
                            density={density}
                            onDensityChange={handleDensityChange}
                            isMobile={isMobile}

                            // Filters
                            selectedGenres={selectedGenres}
                            onGenresChange={setSelectedGenres}
                            availableGenres={availableGenres}

                            selectedNetworks={selectedNetworks}
                            onNetworksChange={setSelectedNetworks}
                            availableNetworks={availableNetworks}

                            selectedContentRatings={selectedContentRatings}
                            onContentRatingsChange={setSelectedContentRatings}
                            availableContentRatings={availableContentRatings}

                            // Series type
                            seriesType={seriesType}
                            onSeriesTypeChange={setSeriesType}

                            // Quick date filters
                            quickDateRange={quickDateRange}
                            onQuickDateRangeChange={handleQuickDateRangeChange}

                            // Random pick
                            onRandomPick={handleRandomPick}
                            canRandomPick={sortedReleases.length > 0}

                            // Watchlist
                            watchlistOnly={effectiveWatchlistOnly}
                            onWatchlistToggle={handleWatchlistToggle}
                            watchlistCount={watchlistCount}
                            watchlistShows={watchlistShows}
                        />

                        {/* Results Summary */}
                        <Typography variant="body" className="text-body-large text-muted-foreground">
                            {hasActiveFiltersExtended ? (
                                <>
                                    <span className="text-primary">{sortedReleases.length}</span>{" "}
                                    of{" "}
                                    <span className="text-secondary-readable">{releases.length}</span>{" "}
                                    shows
                                    {sortedReleases.length !== releases.length && (
                                        <button
                                            onClick={handleResetFilters}
                                            className="ml-2 text-primary hover:underline"
                                        >
                                            Clear filters
                                        </button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span className="text-secondary-readable">{sortedReleases.length}</span>{" "}
                                    scripted shows in this window
                                </>
                            )}
                        </Typography>

                        {/* Loading overlay for date range changes */}
                        {dateRangeLoading && (
                            <div className="fixed inset-0 bg-background/50 backdrop-blur-xs z-50 flex items-center justify-center">
                                <div className="bg-card rounded-lg border p-6 shadow-lg">
                                    <LoadingSpinner />
                                    <p className="text-body text-center mt-4">
                                        Loading {dateRange === "next" ? "upcoming" : "recent"} releases...
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Shows grouped by calendar week in chronological order. */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={`${effectiveViewMode}-${useDateGrouping ? "grouped" : "flat"}`}
                                initial={false}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.2 }}
                            >
                                {useDateGrouping ? (
                                    <DateGroupedReleases
                                        releases={sortedReleases}
                                        renderReleases={renderReleaseCollection}
                                        interstitial={pairingsRail}
                                        referenceDay={referenceDay || ""}
                                    />
                                ) : renderReleaseCollection(sortedReleases)}
                            </motion.div>
                        </AnimatePresence>

                        {/* No filtered results */}
                        {hasNoFilteredResults && (
                            <div className="text-center py-12 space-y-4">
                                <div className="text-muted-foreground mb-4">
                                    <svg
                                        className="mx-auto h-12 w-12"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-h4 font-semibold text-foreground">
                                    No shows match your filters
                                </h3>
                                <p className="text-body text-muted-foreground max-w-md mx-auto">
                                    This list covers the next 30 days only. Try fewer filters, or{" "}
                                    <a
                                        href={archiveSearchHref}
                                        onClick={(event) => handleInternalLinkClick(event, archiveSearchHref)}
                                        className="font-medium text-primary underline underline-offset-2"
                                    >
                                        search the archive
                                    </a>
                                    {" "}for older shows.
                                </p>
                                <button
                                    onClick={handleResetFilters}
                                    className="text-primary hover:underline font-medium"
                                >
                                    Clear all filters
                                </button>
                            </div>
                        )}
                    </>
                )}

                {!useDateGrouping && pairingsRail}

                <WatchlistShelf shows={watchlistShows} />

                </div>
            </main>

            <WhySection />

            <SiteFooter />

            <BackToTopButton />

            <InsightsPanel
                open={insightsOpen}
                onClose={closeInsights}
                insights={insights}
                stats={stats}
                count={count}
                totalCount={totalCount}
                candidateCount={candidateCount}
                lastUpdated={lastUpdated}
                relativeUpdated={insightsSummary.relativeUpdated}
                onGenreToggle={handleGenreToggle}
                onNetworkToggle={handleNetworkToggle}
                selectedGenres={selectedGenres}
                selectedNetworks={selectedNetworks}
                onResetFilters={handleResetFilters}
                windowLabel={windowLabel}
                recommendationPairings={recommendationPairings}
            />
        </div>
    );
};

export default NewApp;

NewApp.propTypes = {
    initialReleasesPayload: PropTypes.shape({
        data: PropTypes.arrayOf(PropTypes.object),
        referenceDay: PropTypes.string,
        lastUpdated: PropTypes.string,
        count: PropTypes.number,
        totalCount: PropTypes.number,
        candidateCount: PropTypes.number,
        stats: PropTypes.object,
        previousStats: PropTypes.object,
        previousLastUpdated: PropTypes.string,
        previousTotalCount: PropTypes.number,
        monthIndex: PropTypes.shape({
            months: PropTypes.arrayOf(PropTypes.object),
            count: PropTypes.number,
            lastUpdated: PropTypes.string,
        }),
    }),
};
